// In-memory virtual file system for a character's OGS inference bundle. The
// loose files (geom/appr decoders, base mesh, rig gather) are fetched once and
// "mounted" under a virtual URL prefix (`/__gspack/<mountId>/<file>`). A
// window.fetch interceptor serves mounted entries from memory, so every existing
// loader — loadMeshAssets / ONNX session creation — consumes the
// bundle through its ordinary `${base}/<file>` fetches, unchanged.
//
// No silent fallbacks: a missing mounted file yields a 404 Response (never a
// network fall-through), per CLAUDE.md UX rules. Mounting is driven by
// ogsContentPacks.js.

import { unzipSync } from 'fflate'

export const GS_PACK_PREFIX = '/__gspack/'

// Parse a .gsh hair pack (a ZIP of the hair_static bundle + a gsh.json manifest)
// into a { filename → Uint8Array } map of its member files. The manifest itself
// is dropped — HairRenderer fetches the members by name (hair_static.ply, the
// ONNX nets, …). Throws if the PLY is absent (a .gsh with no splats is broken).
export function parseGsh(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  const entries = unzipSync(u8)
  const files = new Map()
  for (const [name, data] of Object.entries(entries)) {
    if (name === 'gsh.json') continue
    files.set(name, data)
  }
  if (!files.has('hair_static.ply')) throw new Error('gsh: missing hair_static.ply')
  return files
}

// Map a fetch URL (relative or absolute, same-origin) to a mounted entry [mountId, filename], or null. For tests.
export function resolvePackPath(url) {
  let path = url
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    try {
      const u = new URL(url)
      if (typeof window !== 'undefined' && u.origin !== window.location.origin) return null
      path = u.pathname
    } catch {
      return null
    }
  }
  if (!path.startsWith(GS_PACK_PREFIX)) return null
  const rest = path.slice(GS_PACK_PREFIX.length)
  const slash = rest.indexOf('/')
  if (slash <= 0 || slash === rest.length - 1) return null
  return [rest.slice(0, slash), decodeURIComponent(rest.slice(slash + 1))]
}

// mountId -> Map<filename, Uint8Array>
const mounts = new Map()
let fetchPatched = false

function contentTypeFor(name) {
  return name.endsWith('.json') ? 'application/json' : 'application/octet-stream'
}

function installFetchPatch() {
  if (fetchPatched || typeof window === 'undefined') return
  fetchPatched = true
  const origFetch = window.fetch.bind(window)
  window.fetch = (input, init) => {
    const url = typeof input === 'string'
      ? input
      : (input && typeof input === 'object' && 'url' in input ? input.url : null)
    if (url) {
      const hit = resolvePackPath(url)
      if (hit) {
        const [mountId, name] = hit
        const entry = mounts.get(mountId)?.get(name)
        // 404 (not a throw) so callers' `res.ok` checks surface the missing
        // file with their own context — and never fall through to the network.
        return Promise.resolve(entry
          ? new Response(entry, { status: 200, headers: { 'Content-Type': contentTypeFor(name) } })
          : new Response(`gspack: ${name} not in mounted pack '${mountId}'`, { status: 404 }))
      }
    }
    return origFetch(input, init)
  }
}

// Mount an in-memory { filename → Uint8Array } map under a virtual base so every
// ordinary `${base}/<file>` fetch resolves from memory. Used by ogsContentPacks to
// serve a character's uploaded /ogs bundle to the OGS render component unchanged.
// Re-mounting an id replaces the previous mount. Returns the base.
export function mountFiles(mountId, files) {
  installFetchPatch()
  const map = files instanceof Map ? files : new Map(Object.entries(files))
  mounts.set(mountId, map)
  return `${GS_PACK_PREFIX}${mountId}`
}
